import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import { functions } from "./firebase";
import type {
  BillingConfig,
  Category,
  Coupon,
  NotificationAudience,
} from "@/types";

/**
 * Toda ESCRITA do painel passa por uma Cloud Function do zepadoca-functions.
 *
 * O motivo não é preferência: as regras do Firestore fecham `config/*` e
 * `sellersBilling` com `write: if false`, e abrir a escrita direta ao painel
 * abriria também aos dois aplicativos, que autenticam do mesmo jeito. A
 * validação e a regra de negócio ficam no backend, onde não podem ser burladas.
 *
 * Todas exigem o custom claim `admin`, verificado por `utils/requireAdmin.ts`.
 */

export interface AdminResult {
  success: boolean;
  message?: string;
}

export interface SyncAdminResult {
  admin: boolean;
  /** Verdadeiro quando a chamada mudou o claim. */
  changed: boolean;
  reason:
    | "concedido"
    | "revogado"
    | "ja_era_admin"
    | "fora_da_allowlist"
    | "allowlist_ausente";
}

/**
 * Sincroniza o claim `admin` com a allowlist de UIDs do projeto.
 *
 * É a única callable que alguém **sem** o claim pode chamar com proveito: ela é
 * justamente o que concede. A allowlist vive em `_meta/adminAllowlist`, fora do
 * alcance de qualquer cliente, e é editada pelo Console do Firebase.
 *
 * Ela também revoga: quem saiu da lista perde o acesso na entrada seguinte.
 */
export const syncAdminAccess = call<void, SyncAdminResult>("syncAdminAccess");

/**
 * `timeout` em milissegundos. O SDK desiste em 70 segundos por padrão, e a
 * exportação e a importação dos dados passam disso com folga.
 */
function call<Req, Res>(name: string, options?: { timeout?: number }) {
  const fn = httpsCallable<Req, Res>(functions, name, options);
  return async (payload: Req): Promise<Res> => {
    const result: HttpsCallableResult<Res> = await fn(payload);
    return result.data;
  };
}

export type CategoryInput = Partial<Omit<Category, "id">> & { id?: string };

export const adminUpsertCategory = call<CategoryInput, AdminResult & { id: string }>(
  "adminUpsertCategory",
);

export const adminDeleteCategory = call<{ id: string }, AdminResult>("adminDeleteCategory");

export const adminReorderCategories = call<
  { order: { id: string; order: number }[] },
  AdminResult
>("adminReorderCategories");

export type CouponInput = Partial<Omit<Coupon, "id" | "expirationDate">> & {
  id?: string;
  /** ISO, porque o `Timestamp` do Firestore não atravessa uma callable. */
  expirationDate?: string | null;
};

export const adminUpsertCoupon = call<CouponInput, AdminResult & { id: string }>(
  "adminUpsertCoupon",
);

export const adminDeleteCoupon = call<{ id: string }, AdminResult>("adminDeleteCoupon");

export const adminSetAppConfig = call<
  { clientMinVersion?: string; sellerMinVersion?: string },
  AdminResult
>("adminSetAppConfig");

export const adminSetBillingConfig = call<
  { platformFeePercentage: number },
  AdminResult & { previous?: BillingConfig }
>("adminSetBillingConfig");

export const adminUpdateSeller = call<
  { sellerId: string; data: Record<string, unknown> },
  AdminResult
>("adminUpdateSeller");

export const adminSetSellerActive = call<
  { sellerId: string; isActive: boolean; reason?: string },
  AdminResult
>("adminSetSellerActive");

export const adminUpdateUser = call<
  { userId: string; data: Record<string, unknown> },
  AdminResult
>("adminUpdateUser");

/**
 * Baixa manual de fatura, e o estorno dela.
 *
 * A callable só vira o `paid`. Propagar a quitação para as faturas absorvidas e
 * desbloquear o estabelecimento é do trigger `onPaymentConfirmed`, no backend,
 * que já fazia isso quando o pagamento vinha pelo Mercado Pago.
 */
export const adminMarkBillingPaid = call<
  { billingId: string; paid: boolean; reason?: string },
  AdminResult & { billingId: string; paid: boolean }
>("adminMarkBillingPaid");

/**
 * Ignora uma fatura (`waived: true`) ou volta a cobrá-la (`waived: false`).
 *
 * Ignorar dispensa a comissão do mês e as dívidas acumuladas na fatura, que são
 * ignoradas junto; sem nada a pagar, ela vale como paga. Voltar a cobrar desfaz,
 * junto com as que foram ignoradas com ela.
 */
export const adminWaiveBilling = call<
  { billingId: string; waived: boolean; reason?: string },
  AdminResult & {
    billingId: string;
    waived: boolean;
    waivedBillingIds?: string[];
    restoredBillingIds?: string[];
  }
>("adminWaiveBilling");

/**
 * Liga ou desliga o parceiro de teste. Ligado, as faturas a partir do mês em
 * andamento nascem ignoradas; desligado, a do mês em andamento volta a ser
 * cobrada, e as dos meses de parceria continuam ignoradas.
 */
export const adminSetTestPartner = call<
  { sellerId: string; isTestPartner: boolean },
  AdminResult & {
    sellerId: string;
    isTestPartner: boolean;
    since: { month: number; year: number } | null;
    changedBillingIds: string[];
  }
>("adminSetTestPartner");

export interface RecalculateResult extends AdminResult {
  sellerId: string;
  sellerName: string;
  platformFeePercentage: number;
  monthsProcessed: number;
  monthsCreated: number;
  monthsSkipped: number;
}

export const adminRecalculateBilling = call<
  { sellerId: string; months?: number },
  RecalculateResult
>("adminRecalculateBilling");

export const adminSendInvoiceEmail = call<
  {
    sellerId: string;
    month: number;
    year: number;
    recipientEmail?: string;
    preview?: boolean;
  },
  AdminResult & { sentTo: string; preview: boolean; billingId: string }
>("adminSendInvoiceEmail");

export interface SendNotificationInput {
  audience: NotificationAudience;
  title: string;
  body: string;
  /** Só em `custom`: ids de `users`. */
  userIds?: string[];
  /** Só em `custom`: ids de `sellerUsers`. */
  sellerUserIds?: string[];
  data?: Record<string, string>;
}

export interface SendNotificationResult extends AdminResult {
  notificationId: string;
  targetCount: number;
  recipients: number;
  withoutToken: number;
  devices: number;
  successCount: number;
  failureCount: number;
}

export const adminSendNotification = call<
  SendNotificationInput,
  SendNotificationResult
>("adminSendNotification");

export interface ExportStats {
  collections: number;
  documents: number;
  perCollection: Record<string, number>;
}

/** O que um ZIP de dados tem e de onde ele veio. */
export interface BackupSummary {
  exportedAt: string;
  projectId: string | null;
  bucket: string;
  firestore: ExportStats;
  storage: { files: number; bytes: number };
}

/**
 * O link para baixar um ZIP guardado no Storage. `expiresAt` vem preenchido
 * quando o link vence (o assinado, de uma hora); o link com token não vence.
 */
export interface DownloadLink {
  url: string;
  expiresAt: string | null;
}

/**
 * A exportação e a importação dos dados (Firestore e Storage juntos, num ZIP)
 * levam minutos, e o SDK desiste em 70 segundos por padrão. Os tempos daqui
 * passam um pouco do limite de cada function, para quem desiste ser sempre o
 * servidor, com a mensagem dele.
 */
const DATA_EXPORT_TIMEOUT = 61 * 60_000;
const DATA_INSPECT_TIMEOUT = 10 * 60_000;

export interface ExportDataResult extends AdminResult {
  /** Onde o ZIP ficou guardado, em `backups/exportacoes/`. */
  path: string;
  fileName: string;
  zipBytes: number;
  summary: BackupSummary;
  downloadUrl: string;
  downloadExpiresAt: string | null;
}

/** Passo 1 da exportação: o servidor gera o ZIP no Storage e devolve o link. */
export const adminExportData = call<void, ExportDataResult>("adminExportData", {
  timeout: DATA_EXPORT_TIMEOUT,
});

export interface InspectImportResult extends AdminResult {
  path: string;
  summary: BackupSummary;
  zipBytes: number;
  currentProjectId: string | null;
  currentBucket: string;
  /** O ZIP foi exportado de outro projeto. */
  otherProject: boolean;
  /** O bucket trocado nas URLs do Firestore, quando o ZIP é de outro bucket. */
  rewrittenBucket: { from: string; to: string } | null;
}

/** Lê e confere o ZIP enviado a `imports/{uid}/`, sem gravar nada. */
export const adminInspectImport = call<{ path: string }, InspectImportResult>(
  "adminInspectImport",
  { timeout: DATA_INSPECT_TIMEOUT },
);

/** A frase que confirma a importação. O backend confere a mesma. */
export const IMPORT_CONFIRMATION_PHRASE = "SUBSTITUIR TODOS OS DADOS";

export interface ImportBackupResult extends AdminResult {
  stage: "backup";
  /** Identifica o backup; a troca só segue com ele. */
  importId: string;
  backupPath: string;
  backupFileName: string;
  backupUrl: string;
  backupUrlExpiresAt: string | null;
  backupBytes: number;
  backupSummary: BackupSummary;
}

export interface ImportApplyResult extends AdminResult {
  stage: "apply";
  backupPath: string;
  summary: BackupSummary;
  rewrittenBucket: { from: string; to: string } | null;
  storageWritten: number;
  storageDeleted: number;
  deletedCollections: string[];
  writtenDocuments: number;
  failedDocuments: number;
  failures: { path: string; message: string }[];
}

/**
 * A importação é uma callable só (`adminImportData`), chamada duas vezes: o
 * backup obrigatório de tudo o que está no projeto e, com o `importId` dele,
 * a troca. Separadas, o painel mostra o backup pronto (e o baixa) antes de
 * qualquer coisa ser apagada.
 */
export const adminImportBackup = call<
  { stage: "backup"; path: string; confirmation: string; acceptProjectMismatch: boolean },
  ImportBackupResult
>("adminImportData", { timeout: DATA_EXPORT_TIMEOUT });

export const adminImportApply = call<
  { stage: "apply"; path: string; confirmation: string; importId: string },
  ImportApplyResult
>("adminImportData", { timeout: DATA_EXPORT_TIMEOUT });

/**
 * Traduz o erro de uma callable para uma frase que o administrador entende.
 * O código do Firebase chega como `functions/<codigo>`.
 */
// IA do ambiente: a fila de modelos, as instruções e os limites. A chave de
// API entra por aqui e nunca volta: a leitura traz só se o item tem chave e os
// quatro últimos caracteres dela.

export type AiProviderId = "gemini" | "openai" | "anthropic";

export interface AiCatalogModel {
  id: string;
  label: string;
}

export interface AiProviderInfo {
  id: AiProviderId;
  label: string;
  keyUrl: string;
  defaultModel: string;
  catalog: AiCatalogModel[];
}

export interface AiModelHealth {
  kind: string;
  label: string;
  message: string;
  until: string;
}

export interface AiModelEntry {
  id: string;
  provider: AiProviderId;
  model: string;
  hasOwnKey: boolean;
  keyHint: string | null;
  inheritsFromIndex: number | null;
  usable: boolean;
  health: AiModelHealth | null;
}

export interface AiFeatureView {
  id: string;
  label: string;
  description: string;
  defaultDailyLimit: number;
  maxDailyLimit: number;
  instructionsPlaceholder: string;
  enabled: boolean;
  instructions: string;
  dailyLimit: number;
  usageToday: { reads: number; sellers: number };
}

export interface AiSettingsView {
  models: AiModelEntry[];
  generalInstructions: string;
  features: AiFeatureView[];
  providers: AiProviderInfo[];
  maxModels: number;
  instructionsMax: number;
  configured: boolean;
  updatedAt: string | null;
}

/**
 * Um item da fila como o painel o envia. `apiKey` só vai quando a pessoa
 * digitou uma chave agora; vazia, vale a salva no mesmo item. `useOwnKey:
 * false` herda a chave do item anterior do mesmo provedor.
 */
export interface AiModelDraft {
  id?: string;
  provider: AiProviderId;
  model: string;
  apiKey?: string;
  useOwnKey?: boolean;
}

export interface AiSettingsInput {
  models: AiModelDraft[];
  generalInstructions: string;
  features: Record<string, { enabled: boolean; instructions: string; dailyLimit: number }>;
}

export interface AiModelListResult {
  models: AiCatalogModel[];
  live: boolean;
  error?: string;
}

export type AiTestResult =
  | { ok: true; latencyMs: number; reply: string }
  | { ok: false; latencyMs: number; kind: string; label: string; message: string };

export const adminGetAiSettings = call<void, AiSettingsView>("adminGetAiSettings");

export const adminSetAiSettings = call<AiSettingsInput, AiSettingsView>("adminSetAiSettings");

export const adminListAiModels = call<
  { provider: AiProviderId; apiKey?: string; models?: AiModelDraft[]; index?: number },
  AiModelListResult
>("adminListAiModels");

export const adminTestAiModel = call<{ models: AiModelDraft[]; index: number }, AiTestResult>(
  "adminTestAiModel",
);

export function describeCallableError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? "";
  const message = (error as { message?: string })?.message;

  if (code.includes("permission-denied")) {
    return "Sua conta não tem permissão de administrador para esta ação.";
  }
  if (code.includes("unauthenticated")) {
    return "Sua sessão expirou. Entre novamente.";
  }
  if (code.includes("invalid-argument")) {
    return message || "Dados inválidos. Confira os campos e tente de novo.";
  }
  // Sem mensagem própria, o "not-found" também é o de uma callable que não
  // existe no servidor (ainda não publicada, ou de outra região).
  if (code.includes("not-found")) {
    return message && message.toLowerCase() !== "not-found"
      ? message
      : "O registro não foi encontrado. Ele pode ter sido removido.";
  }
  if (code.includes("failed-precondition")) {
    return message || "A operação não é possível no estado atual do registro.";
  }
  // Erro não tratado no servidor chega só como "internal", que não diz nada.
  if (code.includes("internal")) {
    return message && message.toLowerCase() !== "internal"
      ? message
      : "O servidor não conseguiu concluir a operação. Tente de novo em instantes.";
  }
  if (code.includes("unavailable") || code.includes("deadline-exceeded")) {
    return "Não foi possível falar com o servidor. Verifique a conexão e tente de novo.";
  }
  return message || "Não foi possível concluir a operação.";
}
